import { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Checkbox, Spinner } from '../ui';
import type { ConsentAudience } from '../../lib/consents';
import { OnboardingTermsSummary } from './OnboardingTermsSummary';

export const MARKETING_OPT_OUT_LABEL_CANDIDATE = "I don't want to receive marketing emails";
export const MARKETING_OPT_OUT_LABEL_EMPLOYER = "I don't want to receive marketing emails (optional)";
export const EMPLOYER_TERMS_LABEL =
  "I agree to Reed.ai's Terms of Service and Privacy Notice (required)";
export const EMPLOYER_TERMS_ERROR = 'Please accept the Terms to continue';

export interface TermsAcceptanceStepProps {
  audience: ConsentAudience;
  /**
   * Employer only — state of the required T&Cs tick-box. Candidates have no
   * such box (consent is implied on Continue), so this is ignored for them.
   */
  accepted: boolean;
  /**
   * OPT-OUT checkbox state. `true` = the user does NOT want marketing email.
   * See lib/consents.ts — this is inverted before it is sent to the backend.
   */
  marketingOptOut: boolean;
  onToggleAccepted: (next: boolean) => void;
  onToggleMarketingOptOut: (next: boolean) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  submitError?: string | null;
}

// TermsAcceptanceStep is the final "Gate 1" step of onboarding, shared by the
// candidate, employer and invite-join flows. Candidate and employer diverge
// per the approved Figma frames (C-3 / E-3):
//
//   Candidate  — exactly ONE checkbox (marketing opt-out). Acceptance of the
//                T&Cs and Privacy Notice is IMPLIED by pressing "Create
//                account", so the CTA is never blocked or disabled.
//   Employer   — TWO checkboxes. The T&Cs box is REQUIRED and gates account
//                creation with an inline error; the marketing opt-out is not.
//
// The invite-join flow follows the employer pattern.
export function TermsAcceptanceStep({
  audience,
  accepted,
  marketingOptOut,
  onToggleAccepted,
  onToggleMarketingOptOut,
  onBack,
  onSubmit,
  submitting,
  submitError,
}: TermsAcceptanceStepProps): JSX.Element {
  const isSubmitting = submitting ?? false;
  const isCandidate = audience === 'candidate';
  // Employer-only: the required-terms error appears on submit, not on load, so
  // the form never greets the user with a red message.
  const [showTermsError, setShowTermsError] = useState(false);

  function handleSubmit(): void {
    if (!isCandidate && !accepted) {
      setShowTermsError(true);
      return;
    }
    setShowTermsError(false);
    onSubmit();
  }

  function handleToggleAccepted(next: boolean): void {
    if (next) setShowTermsError(false);
    onToggleAccepted(next);
  }

  return (
    <Card>
      <CardHeader
        title={<h1 className="whitespace-normal">Our Terms</h1>}
        subtitle="Review and accept our Terms."
        trailing={<span className="whitespace-nowrap text-sm text-text-secondary">Step 2 of 2</span>}
        className="flex-col md:flex-row"
      />
      <CardBody>
        <div className="flex flex-col gap-5">
          <OnboardingTermsSummary audience={audience} />

          {isCandidate ? null : (
            <div className="flex flex-col gap-1">
              <Checkbox
                label={EMPLOYER_TERMS_LABEL}
                labelClassName="!text-text-primary"
                checked={accepted}
                disabled={isSubmitting}
                aria-invalid={showTermsError || undefined}
                onChange={(e) => handleToggleAccepted(e.target.checked)}
              />
              {showTermsError ? (
                <p role="alert" className="text-sm text-system-error">
                  {EMPLOYER_TERMS_ERROR}
                </p>
              ) : null}
            </div>
          )}

          {/*
            OPT-OUT box: ticking it means "do not market to me". The inversion
            into the opt-in `marketing` consent happens in lib/consents.ts —
            never here.
          */}
          <Checkbox
            label={isCandidate ? MARKETING_OPT_OUT_LABEL_CANDIDATE : MARKETING_OPT_OUT_LABEL_EMPLOYER}
            labelClassName="!text-text-primary"
            checked={marketingOptOut}
            disabled={isSubmitting}
            onChange={(e) => onToggleMarketingOptOut(e.target.checked)}
          />

          {submitError ? (
            <p className="rounded-md bg-surface-error-container px-3 py-2 text-sm text-system-error">
              {submitError}
            </p>
          ) : null}

          <div className="flex flex-col-reverse gap-3 md:flex-row">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              disabled={isSubmitting}
              onClick={onBack}
            >
              Back
            </Button>
            {/*
              Never gated on the checkboxes. For candidates the terms are
              implied on click; for employers an unticked box surfaces the
              inline error above rather than a dead button.
            */}
            <Button type="button" className="flex-1" disabled={isSubmitting} onClick={handleSubmit}>
              {isSubmitting ? <Spinner size="sm" /> : null}
              {isCandidate ? 'Create account' : 'Complete account setup'}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
